package com.example.mystybag.core.security

import dev.whyoleg.cryptography.BinarySize.Companion.bits
import dev.whyoleg.cryptography.CryptographyProvider
import dev.whyoleg.cryptography.DelicateCryptographyApi
import dev.whyoleg.cryptography.algorithms.AES
import dev.whyoleg.cryptography.algorithms.HMAC
import dev.whyoleg.cryptography.algorithms.MD5
import dev.whyoleg.cryptography.algorithms.RSA
import dev.whyoleg.cryptography.algorithms.SHA1
import dev.whyoleg.cryptography.algorithms.SHA256
import dev.whyoleg.cryptography.algorithms.SHA512
import dev.whyoleg.cryptography.random.CryptographyRandom
import com.example.mystybag.config.ApplicationConfig
import com.example.mystybag.core.expected.CryptoHelperInterface
import com.example.mystybag.core.expected.CryptoStatus
import com.example.mystybag.core.expected.Preferences
import com.example.mystybag.core.expected.getVersion
import com.example.mystybag.core.expected.logException
import com.example.mystybag.core.extensions.ifNotEmpty
import com.example.mystybag.core.helpers.Logger
import io.ktor.util.decodeBase64Bytes
import io.ktor.util.encodeBase64
import io.ktor.utils.io.core.toByteArray
import kotlinx.datetime.Clock
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.intOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlin.experimental.and
import kotlin.experimental.xor
import kotlin.io.encoding.Base64
import kotlin.io.encoding.ExperimentalEncodingApi
import kotlin.math.pow
import kotlin.random.Random

internal expect class SecurityHelper() {
    fun encryptWithAppAESKey(data: ByteArray): String?
    fun decryptWithAppAESKey(data: String): ByteArray?
}

object SecurityUtils {
    fun kvSerial(serial: String): String = serial.decodeBase64Bytes().fixUp().decodeToString()
}

@OptIn(ExperimentalUnsignedTypes::class)
private val localData = ubyteArrayOf(
    0x37u, 0x87u, 0x66u, 0xA8u, 0xB0u, 0x5Au, 0xB8u, 0x71u, 0x44u, 0xEDu, 0x1Cu, 0xA5u, 0xF9u, 0xB7u, 0xABu, 0xE6u,
    0x1Du, 0x39u, 0xE5u, 0x06u, 0x65u, 0x1Fu, 0x4Cu, 0xD8u, 0x6Bu, 0x52u, 0x00u, 0x4Fu, 0x1Au, 0xA1u, 0xA0u, 0x60u,
    0xEAu, 0x86u, 0x81u, 0x0Eu, 0x6Du, 0x04u, 0xF4u, 0xCBu, 0x35u, 0x9Fu, 0x34u, 0x61u, 0x31u, 0x40u, 0xA4u, 0x76u,
    0x1Eu, 0xBEu, 0x0Du, 0x9Cu, 0xEFu, 0x70u, 0x95u, 0x08u, 0xAAu, 0x41u, 0xD4u, 0x88u, 0xC8u, 0x4Au, 0x6Au, 0x7Fu,
    0x30u, 0x89u, 0x0Cu, 0x75u, 0x8Bu, 0x17u, 0xDAu, 0xCDu, 0x7Du, 0xFCu, 0x20u, 0xBBu, 0x1Bu, 0x59u, 0xB6u, 0x05u,
    0x3Au, 0x4Eu, 0x93u, 0x68u, 0x01u, 0xCFu, 0x64u, 0xBAu, 0x23u, 0x07u, 0xE9u, 0xECu, 0xB9u, 0xEBu, 0x32u, 0x15u,
    0xBDu, 0x49u, 0x21u, 0x2Bu, 0x0Au, 0xB3u, 0x6Eu, 0x5Fu, 0xA3u, 0x78u, 0x13u, 0xC5u, 0xF1u, 0x8Eu, 0xE2u, 0xDEu,
    0x14u, 0xD9u, 0x3Eu, 0xCAu, 0x94u, 0x42u, 0x4Bu, 0x5Cu, 0x18u, 0xDFu, 0x8Du, 0x96u, 0xF5u, 0x12u, 0x9Eu, 0xA6u,
    0x2Au, 0xAEu, 0x3Cu, 0x2Cu, 0xF7u, 0x2Du, 0x8Cu, 0xFBu, 0xAFu, 0xF8u, 0x7Cu, 0xB2u, 0x85u, 0x33u, 0x55u, 0x5Du,
    0x16u, 0x10u, 0xC3u, 0xE7u, 0xA2u, 0xF0u, 0x9Bu, 0x74u, 0xC7u, 0x90u, 0x83u, 0xEEu, 0x38u, 0x7Bu, 0x03u, 0xE1u
)

@OptIn(ExperimentalUnsignedTypes::class)
private fun ByteArray.messUp(): ByteArray {

    val bytes = Random.nextBytes(10)
    var s1 = bytes[bytes.size - 3]
    var s2 = bytes.last()

    return ByteArray(this.size + bytes.size) {
        when  {
            it < bytes.size -> bytes[it]
            else -> {
                val index1 = (s1++ and 0x7F).toInt()
                val index2 = (--s2 and 0x7F).toInt()
                (this[it - bytes.size] xor localData[index1].toByte()) xor localData[index2].toByte()
            }
        }
    }
}

@OptIn(ExperimentalUnsignedTypes::class)
fun ByteArray.fixUp(): ByteArray {

    var s1 = this[7]
    var s2 = this[9]

    return ByteArray(this.size - 10) {
        val index1 = (s1++ and 0x7F).toInt()
        val index2 = (--s2 and 0x7F).toInt()
        (this[it + 10] xor localData[index2].toByte()) xor localData[index1].toByte()
    }
}

object Security {

    var cryptoHelper: CryptoHelperInterface? = null

    const val PRIVATE_RSA_KEY_TAG   = "DI4beqvTOYonVpcnahQiJNKU9RbmIlJy"
    const val PUBLIC_RSA_KEY_TAG    = "p0ZK375SS6NfBUWH3tRX5oitjWnHznNblFCv"
    const val AES_KEY_TAG           = "ZPtjU1CcyFabmh08eRbxWak8sz1c5gFwE4nGx4y"
    const val SOME_DATA             = "zjsHm9ES5nsPVh+eCkrLYeDptMnX7NQMcPufR/fKKm8HxnTY8dfxt2Lv"

    init {
       // checkKeys()
    }

    fun checkKeys() {
        if (!isCheckKeysSupported()) return
        val aesTagEncrypted   = Preferences.getString(AES_KEY_TAG, "")
        val privateRsaEnc     = Preferences.getString(PRIVATE_RSA_KEY_TAG, "")
        val publicRsaEnc      = Preferences.getString(PUBLIC_RSA_KEY_TAG, "")

        val aesTagDecryptable = aesTagEncrypted.isNotEmpty() &&
            SecurityHelper().decryptWithAppAESKey(aesTagEncrypted) != null

        // Always ensure we have a decryptable AES key tag (used to protect RSA keys in prefs).
        if (aesTagEncrypted.isEmpty() || !aesTagDecryptable) {
            // Clear old encrypted RSA keys so we don't keep inconsistent state.
            Preferences.remove(PRIVATE_RSA_KEY_TAG)
            Preferences.remove(PUBLIC_RSA_KEY_TAG)
            Preferences.remove(AES_KEY_TAG)

            val provider  = CryptographyProvider.Default
            val randomKey = provider
                .get(AES.GCM)
                .keyGenerator(AES.Key.Size.B256)
                .generateKeyBlocking()
                .encodeToByteArrayBlocking(AES.Key.Format.RAW)
                .messUp()

            val newAesTagEncrypted = SecurityHelper().encryptWithAppAESKey(randomKey)
            if (newAesTagEncrypted.isNullOrEmpty()) {
                return
            }

            Preferences.setString(AES_KEY_TAG, newAesTagEncrypted)
        }

        // Ensure RSA keys exist (used by Security.secureEncrypt/secureDecrypt in Preferences.setSecureString/getSecureString).
        val finalPrivateRsaEnc = Preferences.getString(PRIVATE_RSA_KEY_TAG, "")
        val finalPublicRsaEnc  = Preferences.getString(PUBLIC_RSA_KEY_TAG, "")

        if (finalPrivateRsaEnc.isEmpty() || finalPublicRsaEnc.isEmpty()) {
            val provider = CryptographyProvider.Default
            provider
                .get(RSA.PKCS1)
                .keyPairGenerator(keySize = 2048.bits, digest = SHA256)
                .generateKeyBlocking().also { keyPair ->
                    keyPair.privateKey.encodeToByteArrayBlocking(RSA.PrivateKey.Format.PEM).also { privateKey ->
                        encryptAes(privateKey)?.also { prk ->
                            Preferences.setString(PRIVATE_RSA_KEY_TAG, prk)
                        }
                    }
                    keyPair.publicKey.encodeToByteArrayBlocking(RSA.PublicKey.Format.PEM).also { publicKey ->
                        encryptAes(publicKey)?.also { pbk ->
                            Preferences.setString(PUBLIC_RSA_KEY_TAG, pbk)
                        }
                    }
            }
        }
    }

    fun secureEncrypt(plainText: String): String {
        return encryptWithRsa(plainText, true)
    }

    fun secureDecrypt(cipherText: String): String {
        return decryptWithRsa(cipherText)
    }

    fun secureDecryptByteArray(cipherText: String): ByteArray? {
        return try {
            cryptoHelper?.decryptRSAByteArray(cipherText)?.let { cryptoResult ->
                when (cryptoResult.status) {
                    CryptoStatus.SUCCESS,
                    CryptoStatus.ERROR      -> cryptoResult.result
                    else                    -> null
                }
            }
        }
        catch (e: Exception) {
            logException(e)
            null
        }
    }

    @OptIn(DelicateCryptographyApi::class)
    fun decryptWithRsa(textToDecrypt: String): String {
        return try {
            cryptoHelper?.decryptRSA(textToDecrypt)?.let { cryptoResult ->
                when (cryptoResult.status) {
                    CryptoStatus.SUCCESS,
                    CryptoStatus.ERROR      -> cryptoResult.result
                    else                    -> null
                }
            } ?: getPrivateRSAKey()?.decryptor()?.decryptBlocking(textToDecrypt.decodeBase64Bytes())?.decodeToString() ?: ""
        }
        catch (e: Exception) {
            logException(e)
            ""
        }
    }

    @OptIn(DelicateCryptographyApi::class)
    fun encryptWithRsa(textToEncrypt: String, withClientPublicKey: Boolean = false): String {
        if (textToEncrypt.isEmpty())
            return textToEncrypt

        return try {
            if (withClientPublicKey) {
                cryptoHelper?.encryptRSA(textToEncrypt)?.let { cryptoResult ->
                    when (cryptoResult.status) {
                        CryptoStatus.SUCCESS,
                        CryptoStatus.ERROR      -> cryptoResult.result
                        else                    -> null
                    }
                } ?: getPublicRSAKey()?.encryptor()?.encryptBlocking(textToEncrypt.toByteArray())?.encodeBase64() ?: ""
            }
            else
                "SERVER PUBLIC KEY".ifNotEmpty { pemKey ->
                    CryptographyProvider.Default
                        .get(RSA.PKCS1)
                        .publicKeyDecoder(SHA256)
                        .decodeFromByteArrayBlocking(RSA.PublicKey.Format.PEM, pemKey.toByteArray())
                        .encryptor()
                        .encryptBlocking(textToEncrypt.toByteArray()).encodeBase64()
                } ?: ""
        }
        catch (e: Exception) {
            logException(e)
            ""
        }
    }

    @OptIn(ExperimentalUnsignedTypes::class)
    fun getSalt(): String {
        return try {
            val bytes = ApplicationConfig.salt.decodeBase64Bytes()
            var rand  = bytes[0]
            val len   = bytes.size - 1

            ByteArray(len) {
                val index = (rand++ and 0x7F).toInt()
                bytes[it + 1] xor localData[index].toByte()
            }.decodeToString()
        }
        catch (e: Exception) {
            e.printStackTrace()
            ""
        }
    }

    fun getCleanClientPublicKey(): String = try {
        Preferences.getString(PUBLIC_RSA_KEY_TAG, "").ifNotEmpty {
            decryptAes(it)?.decodeToString()
                ?.replace("-----BEGIN PUBLIC KEY-----", "")
                ?.replace("-----END PUBLIC KEY-----", "")
                ?.replace("\n", "")
                ?.trim()
        } ?: ""
    }
    catch (e: Exception) {
        logException(e)
        ""
    }

    @OptIn(ExperimentalStdlibApi::class)
    fun hashMacSHA256(text: String, secretKey: String): String = try {
        CryptographyProvider.Default
            .get(HMAC)
            .keyDecoder(SHA256)
            .decodeFromByteArrayBlocking(HMAC.Key.Format.RAW, secretKey.toByteArray())
            .signatureGenerator()
            .generateSignatureBlocking(text.toByteArray())
            .toHexString()
    }
    catch (e: Exception) {
        logException(e)
        ""
    }

    /**
     * Returns HMAC-SHA256 hash of the password using ApplicationConfig.salt as key.
     * Use this for all API requests that send a password field (login, registration, reset/change password).
     * On JS uses Web Crypto (no CryptographyProvider); on other platforms uses hashMacSHA256.
     */
    suspend fun hashPasswordForRequest(password: String): String =
        SecurityCryptoImpl.hashPasswordForRequest(password)

    @OptIn(ExperimentalStdlibApi::class, DelicateCryptographyApi::class)
    fun md5Sum(text: String): String = try {
        CryptographyProvider.Default
            .get(MD5)
            .hasher()
            .hashBlocking(text.toByteArray())
            .toHexString()
    }
    catch (e: Exception) {
        logException(e)
        ""
    }

    fun generateSecretKey(): ByteArray? = try {
        CryptographyProvider.Default
            .get(AES.CBC)
            .keyGenerator(AES.Key.Size.B128)
            .generateKeyBlocking()
            .encodeToByteArrayBlocking(AES.Key.Format.RAW)
    }
    catch (e: Exception) {
        logException(e)
        null
    }

    fun getSecretKey() = try {
        "Secret key".ifNotEmpty {
            decryptWithRsa(it).toByteArray()
        }
    }
    catch (e: Exception) {
        logException(e)
        null
    }

    @OptIn(DelicateCryptographyApi::class)
    fun encryptWithAes(message: String, aesKey: ByteArray): List<ByteArray> {
        val iv = CryptographyRandom.nextBytes(16)
        return listOf(
            iv,
            CryptographyProvider.Default
                .get(AES.CBC)
                .keyDecoder()
                .decodeFromByteArrayBlocking(AES.Key.Format.RAW, aesKey)
                .cipher()
                .encryptWithIvBlocking(iv, message.toByteArray())
        )
    }

    fun generateResponse(challenge: String, time: Long = Clock.System.now().toEpochMilliseconds()): String? {
        var result: String? = null
        try {
            val ocraSuite = "OCRA-1:HOTP-SHA512-8:C-QN08"
            val codeDigits = 8
            val counterLength = 8
            val questionLength = 128
            val ocraSuiteLength = ocraSuite.toByteArray().size
            val counter = (time / 30).toString().take(8).toLong().toByteArray() ?: return null

            // Remember to add "1" for the "00" byte delimiter
            val msg = ByteArray(ocraSuiteLength + counterLength + questionLength + 1)

            // Put the bytes of "ocraSuite" parameters into the message
            var bArray = ocraSuite.toByteArray()
            bArray.copyInto(msg)

            // Put the bytes of "Counter" to the message
            counter.copyInto(msg, ocraSuiteLength + 1)

            // Put the bytes of "question" to the message
            bArray = challenge.toLong().toByteArray() ?: return null
            val bChallenge = ByteArray(questionLength)
            bArray.copyInto(bChallenge)
            bChallenge.copyInto(msg, ocraSuiteLength + 1 + counterLength, 0, bArray.size)
            val hash: ByteArray = hashMacSHA512(msg) ?: return null

            // put selected bytes into result int
            val offset = hash[hash.size - 1].toInt() and 0xf
            val binary = hash[offset].toInt() and 0x7f shl 24 or
                (hash[offset + 1].toInt() and 0xff shl 16) or
                (hash[offset + 2].toInt() and 0xff shl 8) or
                (hash[offset + 3].toInt() and 0xff)
            val otp: Int = binary % 10f.pow(codeDigits).toInt()
            result = otp.toString().padStart(codeDigits, '0')
        }
        catch (e: Exception) {
            logException(e)
        }
        return result
    }

    @OptIn(ExperimentalEncodingApi::class, DelicateCryptographyApi::class)
    suspend fun decryptJWE(jwe: String): String {

        try {
            val (header, aes, iv, body, sig) = jwe.split(".")

            val base64AP    = Base64.UrlSafe.withPadding(Base64.PaddingOption.ABSENT_OPTIONAL)
            val aadBytes    = header.toByteArray()
            val aesBytes    = base64AP.decode(aes)
            val bodyBytes   = base64AP.decode(body)
            val ivBytes     = base64AP.decode(iv)
            val sigBytes    = base64AP.decode(sig)

            suspend fun decryptBody(key: ByteArray): String =
                CryptographyProvider.Default
                    .get(AES.GCM)
                    .keyDecoder()
                    .decodeFromByteArray(AES.Key.Format.RAW, key)
                    .cipher()
                    .decryptWithIv(ivBytes, bodyBytes + sigBytes, aadBytes)
                    .decodeToString()

            return cryptoHelper?.decryptJWE(aesBytes)?.let { cryptoResult ->
                when (cryptoResult.status) {
                    CryptoStatus.SUCCESS -> {
                        cryptoResult.result?.let {
                            decryptBody(it)
                        } ?: ""
                    }
                    CryptoStatus.ERROR  -> ""
                    else                -> null
                }
            } ?: run {
                 Preferences.getString(PRIVATE_RSA_KEY_TAG, "").ifNotEmpty { keyStr ->
                     decryptAes(keyStr)?.let { key ->
                         CryptographyProvider.Default
                             .get(RSA.OAEP)
                             .privateKeyDecoder(SHA1)
                             .decodeFromByteArray(RSA.PrivateKey.Format.PEM, key)
                             .decryptor()
                             .decrypt(aesBytes)
                             .let {
                                 decryptBody(it)
                             }
                     }
                 } ?: ""
             }
        }
        catch (e: Exception) {
            logException(e)
            return ""
        }
    }

    suspend fun sign(data: ByteArray): ByteArray? = try {
        if (cryptoHelper == null)
             getPrivateRSAKey()?.signatureGenerator()?.generateSignature(data)
        else
             cryptoHelper?.sign(data)?.let { cryptoResult ->
                 when (cryptoResult.status) {
                    CryptoStatus.KEY -> getPrivateRSAKey()?.signatureGenerator()?.generateSignature(data)
                    else             -> cryptoResult.result
                }
            }
    }
    catch (e: Exception) {
        logException(e)
        null
    }

    suspend fun signRequest(request: String): String? = try {
        SecurityCryptoImpl.signRequest(request)
    } catch (e: Exception) {
        logException(e)
        null
    }

    @OptIn(DelicateCryptographyApi::class)
    suspend fun encryptRequest(request: String): String? = try {
        SecurityCryptoImpl.encryptRequest(request)
    } catch (e: Exception) {
        logException(e)
        null
    }

    /** Platform-specific: full RSA on Android/iOS, encoding on JS. Use this for secure preference storage. */
    fun secureEncryptPreference(plainText: String): String = SecurityCryptoImpl.secureEncryptPreference(plainText)

    /** Platform-specific: decrypt value from secure preference storage. */
    fun secureDecryptPreference(cipherText: String): String = SecurityCryptoImpl.secureDecryptPreference(cipherText)

    suspend fun sign(text: String): String = try {
        sign(text.toByteArray())?.encodeBase64() ?: ""
    }
    catch (e: Exception) {
        logException(e)
        ""
    }

    @OptIn(DelicateCryptographyApi::class)
    fun signPkcs1Sha256Base64(data: ByteArray, privateKeyPem: String): String {
        return try {
            if (privateKeyPem.isBlank()) {
                ""
            } else {
                CryptographyProvider.Default
                    .get(RSA.PKCS1)
                    .privateKeyDecoder(SHA256)
                    .decodeFromByteArrayBlocking(RSA.PrivateKey.Format.PEM, privateKeyPem.toByteArray())
                    .signatureGenerator()
                    .generateSignatureBlocking(data)
                    .encodeBase64()
            }
        }
        catch (e: Exception) {
            logException(e)
            ""
        }
    }

    suspend fun signPkcs1Sha256Base64Async(data: ByteArray, privateKeyPem: String): String {
        if (privateKeyPem.isBlank()) return ""
        return try {
            SecurityCryptoImpl.signPkcs1Sha256Base64(data, privateKeyPem)
                ?: signPkcs1Sha256Base64(data, privateKeyPem)
        } catch (e: Exception) {
            logException(e)
            signPkcs1Sha256Base64(data, privateKeyPem)
        }
    }

    @OptIn(ExperimentalEncodingApi::class)
    fun validateJWT() {
        /*ModelsData.walletProduct = ApplicationConfig.walletProduct
        check(MystyBagLibSDK.appKey.isNotEmpty()) { "Invalid ApplicationToken (Empty)" }

        val parts = MystyBagLibSDK.appKey.split(".").toMutableList()
        check(parts.size == 3) { "Invalid ApplicationToken (Size)" }

        val signatureBase64  = parts.deleteLast()
        val headerAndPayload = parts.joinToString(".")
        val base64AP         = Base64.UrlSafe.withPadding(Base64.PaddingOption.ABSENT_OPTIONAL)
        var prodMessage      = ""

        val productID = try {
            base64AP
                .decode(parts[1])
                .decodeToString()
                .extractClaim("walletProduct") ?: ""
        }
        catch (ex: Exception) {
            prodMessage = ex.message ?: "X"
            logException(ex)
            ""
        }

        check(productID.isNotEmpty()) { "Invalid ApplicationToken (Product)[$prodMessage]" }
        ModelsData.walletProduct = productID

        CoroutineScope(Dispatchers.Default + globalExceptionHandler).launch(CoroutineName("validateJWT")) {

            Preferences.getString(PREFERENCES_SERVER_PUBLIC_KEY, "").takeUnless {
                //skip verification if server's public key is in the old format (not PEM)
                it.isEmpty() || it.contains("RSAKeyValue")
            }?.also { pemKey ->

                val signature   = base64AP.decode(signatureBase64)
                val data        = headerAndPayload.toByteArray()
                var message     = ""

                val isValid = try {
                    CryptographyProvider.Default
                        .get(RSA.PKCS1)
                        .publicKeyDecoder(SHA256)
                        .decodeFromByteArray(RSA.PublicKey.Format.PEM, pemKey.toByteArray())
                        .signatureVerifier()
                        .tryVerifySignature(data, signature)
                }
                catch (ex: Exception) {
                    message = ex.message ?: "X"
                    logException(ex)
                    false
                }

                check(isValid) { "Invalid ApplicationToken (Invalid)[$message]" }
            }
        }*/
    }

    private fun String.extractClaim(claim: String): String? {
        return try {
            val jsonElement = Json.parseToJsonElement(this)
            val primitive   = jsonElement.jsonObject[claim]?.jsonPrimitive ?: return null

            when  {
                primitive.isString -> primitive.contentOrNull
                else               -> primitive.intOrNull?.toString()
            }
        }
        catch (ex: Exception) {
            logException(ex)
            null
        }
    }

    private fun hashMacSHA512(data: ByteArray): ByteArray? = try {
        getSecretKey()?.let {
            CryptographyProvider.Default
                .get(HMAC)
                .keyDecoder(SHA512)
                .decodeFromByteArrayBlocking(HMAC.Key.Format.RAW, it)
                .signatureGenerator()
                .generateSignatureBlocking(data)
        }
    }
    catch (e: Exception) {
        logException(e)
        null
    }

    @OptIn(ExperimentalStdlibApi::class)
    private fun Long.toByteArray(): ByteArray? {
        return try {
            this.toHexString(format = HexFormat {
                number.removeLeadingZeros = false
            }).chunked(2).map {
                it.hexToByte()
            }.toByteArray()
        }
        catch (e: Exception) {
            logException(e)
            null
        }
    }

    private fun getPrivateRSAKey(): RSA.PKCS1.PrivateKey? = try {
        Preferences.getString(PRIVATE_RSA_KEY_TAG, "").ifNotEmpty {
            decryptAes(it)?.let { key ->
                CryptographyProvider.Default
                    .get(RSA.PKCS1)
                    .privateKeyDecoder(SHA256)
                    .decodeFromByteArrayBlocking(RSA.PrivateKey.Format.PEM, key)
            }
        }
    }
    catch (e: Exception) {
        logException(e)
        null
    }

    private fun getPublicRSAKey(): RSA.PKCS1.PublicKey? = try {
        Preferences.getString(PUBLIC_RSA_KEY_TAG, "").ifNotEmpty {
            decryptAes(it)?.let { key ->
                CryptographyProvider.Default
                    .get(RSA.PKCS1)
                    .publicKeyDecoder(SHA256)
                    .decodeFromByteArrayBlocking(RSA.PublicKey.Format.PEM, key)
            }
        }
    }
    catch (e: Exception) {
        logException(e)
        null
    }

    private fun decryptAes(data: String): ByteArray? = try {
        getAESKey()?.cipher()?.decryptBlocking(data.decodeBase64Bytes())
    }
    catch (e: Exception) {
        logException(e)
        null
    }

    private fun encryptAes(data: ByteArray): String? = try {
        getAESKey()?.cipher()?.encryptBlocking(data)?.encodeBase64()
    }
    catch (e: Exception) {
        logException(e)
        null
    }

    private fun getAESKey(): AES.GCM.Key? = try {
        Preferences.getString(AES_KEY_TAG, "").ifNotEmpty {
            SecurityHelper().decryptWithAppAESKey(it)?.let { key ->
                CryptographyProvider.Default
                    .get(AES.GCM)
                    .keyDecoder()
                    .decodeFromByteArrayBlocking(AES.Key.Format.RAW, key.fixUp())
            }
        }
    }
    catch (e: Exception) {
        logException(e)
        null
    }
}